const stripQuotes = (value) =>
  String(value || "")
    .replace(/'/g, "")
    .replace(/"/g, "")
    .trim();

export const normalizePermission = stripQuotes;

export const hasPermission = (permission, permissions = []) => {
  const normalizedPermission = normalizePermission(permission);
  if (!normalizedPermission) return true;
  return (
    Array.isArray(permissions) && permissions.includes(normalizedPermission)
  );
};

// Módulo contratado pela empresa atual (ex.: "GESTAOCERTIDOES"); pausado conta como ausente.
export const hasModulo = (modulo, state = {}) => {
  if (!modulo) return true;
  const tenant = state.inquilino || state.user?.inquilino || {};
  return (tenant.modulosContratacao || []).some(
    (m) => m?.tipo === modulo && m?.ativo !== false
  );
};

export const normalizeMenuPath = (path) => {
  const normalized = String(path || "")
    .split("?")[0]
    .split("#")[0]
    .replace(/\/+$/, "");

  return (normalized || "/").toLowerCase();
};

export const routeMatchesMenuLink = (routePath, menuLink) => {
  const path = normalizeMenuPath(routePath);
  const link = normalizeMenuPath(menuLink);

  return path === link || path.startsWith(`${link}/`);
};

const itemHasAccessibleChildren = (item, permissions) => {
  const children = item?.items || item?.children || [];
  return children.some((child) => isMenuItemAccessible(child, permissions));
};

export const isMenuItemAccessible = (item, permissions = []) => {
  if (!item) return false;
  if (!hasPermission(item.can, permissions)) return false;

  const children = item.items || item.children;
  if (Array.isArray(children) && children.length > 0 && !item.link) {
    return itemHasAccessibleChildren(item, permissions);
  }

  return true;
};

const filterChildren = (children, permissions) => {
  if (!Array.isArray(children)) return undefined;

  return children
    .filter((child) => isMenuItemAccessible(child, permissions))
    .map((child) => ({
      ...child,
      items: filterChildren(child.items, permissions),
      children: filterChildren(child.children, permissions),
    }));
};

export const filterAccessibleMenuItems = (items = [], permissions = []) => {
  if (!Array.isArray(items)) return [];

  return items
    .filter((item) => isMenuItemAccessible(item, permissions))
    .map((item) => ({
      ...item,
      items: filterChildren(item.items, permissions),
      children: filterChildren(item.children, permissions),
    }));
};

const collectLinksFromItems = (items = [], groupTitle) => {
  if (!Array.isArray(items)) return [];

  return items.reduce((links, item) => {
    if (item?.link) {
      links.push({
        ...item,
        groupTitle,
        rootLink: item.link,
      });
    }

    if (Array.isArray(item?.items)) {
      links.push(...collectLinksFromItems(item.items, groupTitle));
    }

    if (Array.isArray(item?.children)) {
      links.push(...collectLinksFromItems(item.children, groupTitle));
    }

    return links;
  }, []);
};

export const buildMenuNavigationGroups = (items = [], permissions = []) => {
  return filterAccessibleMenuItems(items, permissions)
    .map((group) => ({
      ...group,
      links: collectLinksFromItems(
        group.items || group.children || [],
        group.title
      ),
    }))
    .filter((group) => group.links.length > 0);
};

// O mesmo link pode estar em mais de um grupo (Certidões fica em Comercial e em Oportunidades
// públicas): no empate, vale o grupo declarado na rota.
export const findActiveMenuLink = (
  groups = [],
  routePath = "",
  routeGroup = null
) => {
  const links = groups.reduce((allLinks, group) => {
    group.links.forEach((link) => allLinks.push({ ...link, group }));
    return allLinks;
  }, []);
  const doGrupoDaRota = (link) =>
    !!routeGroup &&
    (link.group?.title === routeGroup || link.group?.group === routeGroup);

  return links
    .filter((link) => routeMatchesMenuLink(routePath, link.link))
    .sort(
      (a, b) =>
        normalizeMenuPath(b.link).length - normalizeMenuPath(a.link).length ||
        Number(doGrupoDaRota(b)) - Number(doGrupoDaRota(a))
    )[0];
};
